import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { Industry } from '../../../stores/industryStore';
import { useProfileStore } from '../../../stores/profileStore';

interface Props {
  industry?: Industry | null;
  onClose: () => void;
}

const AddIndustryModal: React.FC<Props> = ({ industry = null, onClose }) => {
  const { t } = useTranslation(['common', 'admin']);
  const [label, setLabel] = useState(industry?.label || '');
  const [description, setDescription] = useState(industry?.industry_description || '');
  const [saving, setSaving] = useState(false);

  const { suggestNewIndustry } = useProfileStore();

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await suggestNewIndustry({ label, description });

      onClose();
    } catch (err) {
      console.error('Failed to upsert industry', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[36rem]">
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {industry ? t('edit_industry', { ns: 'admin' }) : t('add_new_industry', { ns: 'common' })}
      </h2>

      <div className="flex flex-col gap-3">
        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.label', { ns: 'admin' })}
        </label>
        <input
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />

        <label className="text-sm font-semibold text-gray-700">
          {t('table_fields.description', { ns: 'admin' })}
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary h-24"
        />
      </div>

      <div className="flex flex-row md:flex-row justify-end gap-4 mt-6">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {t('common:form_actions.cancel', { ns: 'common' })}
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
          disabled={saving || !label}
        >
          {saving
            ? t('loading', { ns: 'common' })
            : t('common:form_actions.save', { ns: 'common' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AddIndustryModal;
